import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import RequestHelper from "../../../helpers/RequestHelper";
import Rows from "../../../library/components/layout/Rows";
import { getGenelDurumString } from "../../../features/loadingMessageSlice";
import { getCurrentUser } from "../../../features/currentUserSlice";
import GenelDurumHelper, { GenelDurumLoginStateHelper } from "../../../helpers/GenelDurumHelper";
import { IMainScreenProps, SubPageLayout } from "../../../components/layout/MainLayout";
import { mainScreenLocations } from "../../../app/mainScreens";
import { useTranslation } from "react-i18next";
import TranslationHelper from "../../../helpers/TranslationHelper";
import Columns from "../../../library/components/layout/Columns";
import { DizilimModel } from "../../../models/businessModels/DizilimModel";
import { Button } from "@mui/material";
import Cell from "../../../library/components/layout/Cell";
import { GuidHelper } from "../../../library/helpers/GuidHelper";
import { DizilimHesapModel } from "../../../models/businessModels/DizilimHesapModel";
import { HesapBilgiVeKullaniciModel } from "../../../models/businessModels/HesapBilgiVeKullaniciModel";
import DateTimeHelper from "../../../library/helpers/DateTimeHelper";
import { CsmHeader } from "../../../library/components/mui/CsmHeader";
import { BusinessHelper } from "../../../helpers/BusinessHelper";
import { SesGrupModel } from "../../../models/businessModels/SesGrupModel";
import DizilimDisplayer from "./DizilimDisplayer";
import JsonHelper from "../../../library/helpers/JsonHelper";
import { EnmYetki } from "../../../models/enums/EnmYetki";
import { CsmText } from "../../../library/components/mui/CsmText";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import ContentPasteIcon from "@mui/icons-material/ContentPaste";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import VerticalAlignTopIcon from "@mui/icons-material/VerticalAlignTop";
import AlertHelper from "../../../library/helpers/AlertHelper";

const dizilimClipboardMarker = "korocanDizilim";

type DizilimClipboardData = {
    marker: typeof dizilimClipboardMarker,
    positions: { hesapId: string, locationX: number, locationY: number }[],
};

const katsayi = Math.ceil(BusinessHelper.KonserDizilim.AvatarWidth / BusinessHelper.KonserDizilim.DizilimMainUnitInPixels);

function KonserDizilimScreen() {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const { t, i18n } = useTranslation();
    const translate = TranslationHelper.TransFunc(t);
    const requestHelper = RequestHelper(navigate, dispatch, translate);

    const genelDurum = GenelDurumHelper.StringToModel(useAppSelector(getGenelDurumString)!)!;
    const user = useAppSelector(getCurrentUser);

    useEffect(() => {
        if (!user.token || genelDurum == null) {
            GenelDurumLoginStateHelper.GenelDurumYukleVeKoroAyarla(requestHelper, navigate, dispatch, user, genelDurum);
        }
    })

    if (!user.token || genelDurum == null) {
        return <></>;
    }

    const genelDurumHelper = new GenelDurumHelper(genelDurum, user, translate, navigate, dispatch, i18n, requestHelper);

    return <KonserDizilimScreenInner genelDurumHelper={genelDurumHelper} />
}

function KonserDizilimScreenInner(props: IMainScreenProps) {
    const { genelDurumHelper } = props;
    const { konserId } = useParams();

    const [seciliSarkiId, setSeciliSarkiId] = useState<string | null | undefined>(undefined);
    const [dizilimList, setDizilimList] = useState<DizilimModel[]>(genelDurumHelper.SeciliKoroModel.dizilimModelList.where(e => e.konserId === konserId));

    const yetkiVar = genelDurumHelper.HasYetki(EnmYetki["Dizilim Yönetim"]);

    const konser = useMemo(() => genelDurumHelper.SeciliKoroModel.tumKonserler.single(e => e.id === konserId), [konserId]);
    const konserinHesapBilgileri = useMemo(() => {
        const hesapList = genelDurumHelper.SeciliKoroModel.hesapBilgiList.where(e => e.kadroVeSesGrubuBilgiModelList.any(t => t.kadroModelId === konser.kadroModelId));
        const hesapVeSesGrupList = hesapList.select(e => ({
            hesap: e,
            sesGrup: genelDurumHelper.SesGruplariniGetir([e.kadroVeSesGrubuBilgiModelList.single(t => t.kadroModelId === konser.kadroModelId).altSesGrupModelId]).single(),
        }));
        return hesapVeSesGrupList.orderBy(e => e.sesGrup.sira);
    }, [konserId]);

    const seciliSarki = seciliSarkiId === undefined ? undefined : genelDurumHelper.SeciliKoroModel.tumSarkilar.singleOrDefault(e => e.id === seciliSarkiId);
    const seciliSarkiDizilimi = dizilimList.singleOrDefault(e => e.sarkiId === seciliSarkiId);

    async function sarkiDizilimEkle(sarkiId: string | null) {
        const yeniModel = yeniDizilimOlustur(sarkiId, konserId!, konserinHesapBilgileri, genelDurumHelper);
        const response = await genelDurumHelper.requestHelper.YonetimDizilim.Ekle(yeniModel);

        if (!response.successfull) return;

        GenelDurumLoginStateHelper.GenelDurumStateGuncelle(genelDurumHelper.dispatch, genelDurumHelper.genelDurum,
            (newGenelDurum) => {
                const seciliKoro = BusinessHelper.Genel.SeciliKoroGetir(newGenelDurum, genelDurumHelper.user)!;

                seciliKoro.dizilimModelList = [
                    ...seciliKoro.dizilimModelList,
                    yeniModel
                ]
            });
    }

    function dizilimChanged(dizilim: DizilimModel): void {
        const eskiler = dizilimList.where(e => e.id !== dizilim.id);
        setDizilimList([
            ...eskiler,
            dizilim
        ]);
    }

    async function kaydet(dizilim: DizilimModel) {
        const response = await genelDurumHelper.requestHelper.YonetimDizilim.Guncelle(dizilim);

        if (!response.successfull) return;

        GenelDurumLoginStateHelper.GenelDurumStateGuncelle(genelDurumHelper.dispatch, genelDurumHelper.genelDurum,
            (newGenelDurum) => {
                const seciliKoro = BusinessHelper.Genel.SeciliKoroGetir(newGenelDurum, genelDurumHelper.user)!;
                const digerDizilimler = seciliKoro.dizilimModelList.where(e => e.id !== dizilim.id);

                seciliKoro.dizilimModelList = [
                    ...digerDizilimler,
                    dizilim
                ]
            });
    }

    function sirala(seciliSarkiDizilimi: DizilimModel): void {
        const yeniDizilim = JsonHelper.deepCopy(seciliSarkiDizilimi);

        const groups = konserinHesapBilgileri.groupBy(e => e.sesGrup.id).reverse();
        const groupKeys = groups.select(e => e.key).distinct();

        for (let siraY = 0; siraY < groupKeys.length; siraY++) {
            const grubunKoristleri = groups.single(e => e.key === groupKeys[siraY]).list;
            grubunKoristleri.forEach((e, i) => {
                const found = yeniDizilim.dizilimHesapModelList.single(t => t.hesapId === e.hesap.id);
                found.locationY = siraY * katsayi;
                found.locationX = i * katsayi;
            });
        }

        dizilimChanged(yeniDizilim);
    }

    async function kopyala(seciliSarkiDizilimi: DizilimModel): Promise<void> {
        const data: DizilimClipboardData = {
            marker: dizilimClipboardMarker,
            positions: seciliSarkiDizilimi.dizilimHesapModelList.select(e => ({ hesapId: e.hesapId, locationX: e.locationX, locationY: e.locationY })),
        };

        await navigator.clipboard.writeText(JsonHelper.stringify(data));
    }

    async function yapistir(seciliSarkiDizilimi: DizilimModel): Promise<void> {
        const text = await navigator.clipboard.readText();
        const data = JsonHelper.parse<DizilimClipboardData>(text);

        if (data == null || data.marker !== dizilimClipboardMarker || data.positions == null) {
            await AlertHelper.ShowMessage("Dikkat", "Panoda geçerli bir dizilim bulunamadı");
            return;
        }

        const yeniDizilim = JsonHelper.deepCopy(seciliSarkiDizilimi);

        // Bizdeki her kişiyi panoda ara: bulunanların konumunu uygula, bulunmayanlara dokunma.
        // Panoda olup bizde olmayanlar zaten döngüye girmediğinden yok sayılır.
        for (const dizilimHesap of yeniDizilim.dizilimHesapModelList) {
            const found = data.positions.firstOrDefault(e => e.hesapId === dizilimHesap.hesapId);
            if (found == null) continue;
            dizilimHesap.locationX = found.locationX;
            dizilimHesap.locationY = found.locationY;
        }

        dizilimChanged(yeniDizilim);
    }

    function hareketEttir(seciliSarkiDizilimi: DizilimModel, dx: number, dy: number): void {
        const hesaplar = seciliSarkiDizilimi.dizilimHesapModelList;

        // Sol/üst sınır kontrolü: herhangi biri kenardaysa (0) o yönde kaydırma
        if (dx < 0 && hesaplar.any(e => e.locationX === 0)) return;
        if (dy < 0 && hesaplar.any(e => e.locationY === 0)) return;

        const yeniDizilim = JsonHelper.deepCopy(seciliSarkiDizilimi);
        yeniDizilim.dizilimHesapModelList.forEach(e => {
            e.locationX += dx;
            e.locationY += dy;
        });

        dizilimChanged(yeniDizilim);
    }

    function trim(seciliSarkiDizilimi: DizilimModel): void {
        const hesaplar = seciliSarkiDizilimi.dizilimHesapModelList;
        if (hesaplar.length === 0) return;

        const minX = hesaplar.min(e => e.locationX)!;
        const minY = hesaplar.min(e => e.locationY)!;
        if (minX === 0 && minY === 0) return;

        const yeniDizilim = JsonHelper.deepCopy(seciliSarkiDizilimi);
        yeniDizilim.dizilimHesapModelList.forEach(e => {
            e.locationX -= minX;
            e.locationY -= minY;
        });

        dizilimChanged(yeniDizilim);
    }

    const kadroHesapIdList = konserinHesapBilgileri.select(e => e.hesap.id);
    const dizilimHesapIdList = (seciliSarkiDizilimi?.dizilimHesapModelList ?? []).select(e => e.hesapId);

    const eksikler = kadroHesapIdList.except(dizilimHesapIdList);
    const fazlalar = dizilimHesapIdList.except(kadroHesapIdList);

    function eksikFazlaDuzelt(seciliSarkiDizilimi: DizilimModel): void {
        const yeniDizilim = JsonHelper.deepCopy(seciliSarkiDizilimi);

        yeniDizilim.dizilimHesapModelList = yeniDizilim.dizilimHesapModelList.where(e => fazlalar.contains(e.hesapId) === false);

        for (let i = 0; i < eksikler.length; i++)
            yeniDizilim.dizilimHesapModelList.push(dizilimHesapOlustur(i * katsayi, 10 * katsayi, yeniDizilim, genelDurumHelper.SeciliKoroModel.hesapBilgiList.single(e => e.id === eksikler[i]), genelDurumHelper));

        dizilimChanged(yeniDizilim);
    }

    return (
        <SubPageLayout
            title={konser.isim + " Dizilimleri"}
            previousLocations={[{ location: mainScreenLocations.konserYonetim }]}
            genelDurumHelper={genelDurumHelper}
            hideHeader={false}
            hideMenu={false}>
            <Rows flex>
                <Columns flex>
                    <Rows pt={10}>
                        <Button onClick={() => setSeciliSarkiId(null)} color={seciliSarkiId === null ? "secondary" : "primary"}> Genel Dizilim</Button>
                        {konser.konserSarkiModelList.map(konserSarki => {
                            const sarki = genelDurumHelper.SeciliKoroModel.tumSarkilar.single(e => e.id === konserSarki.sarkiModelId);
                            return <Cell key={sarki.id} hasLightTopBorder mt={10} pt={10}>
                                <Button fullWidth onClick={() => setSeciliSarkiId(sarki.id)} color={seciliSarkiId === sarki.id ? "secondary" : "primary"}>{sarki.isim}</Button>
                            </Cell>;
                        })}
                    </Rows>
                    {seciliSarkiId !== undefined &&
                        <Rows flex p={10}>
                            <Columns gap={10} style={{ placeItems: "center" }}>
                                <CsmText>Eser:</CsmText>
                                <CsmHeader>{seciliSarki?.isim ?? "Genel Dizilim"}</CsmHeader>
                                <Cell width={10}></Cell>
                                <CsmText>Konser:</CsmText>
                                <CsmHeader>{konser.isim}</CsmHeader>
                                {seciliSarkiDizilimi === null && <CsmText note>Özel dizilim tanımlanmamıştır</CsmText>}
                                {yetkiVar &&
                                    <>
                                        {seciliSarkiDizilimi === null && <Button color="secondary" variant="contained" onClick={() => sarkiDizilimEkle(seciliSarkiId)}>Dizilim Oluştur</Button>}
                                        {seciliSarkiDizilimi !== null && <Button variant="contained" onClick={() => kaydet(seciliSarkiDizilimi)} color={"primary"}>Kaydet</Button>}
                                        {seciliSarkiDizilimi !== null && <Button variant="outlined" onClick={() => sirala(seciliSarkiDizilimi)} color={"primary"}>Sırala</Button>}
                                        {seciliSarkiDizilimi !== null && <Button variant="outlined" onClick={() => kopyala(seciliSarkiDizilimi)} color={"primary"} title="Kopyala"><ContentCopyIcon /></Button>}
                                        {seciliSarkiDizilimi !== null && <Button variant="outlined" onClick={() => yapistir(seciliSarkiDizilimi)} color={"primary"} title="Yapıştır"><ContentPasteIcon /></Button>}
                                        {seciliSarkiDizilimi !== null && <Button variant="outlined" onClick={() => hareketEttir(seciliSarkiDizilimi, -1, 0)} color={"primary"} title="Sola"><ArrowBackIcon /></Button>}
                                        {seciliSarkiDizilimi !== null && <Button variant="outlined" onClick={() => hareketEttir(seciliSarkiDizilimi, 0, -1)} color={"primary"} title="Yukarı"><ArrowUpwardIcon /></Button>}
                                        {seciliSarkiDizilimi !== null && <Button variant="outlined" onClick={() => hareketEttir(seciliSarkiDizilimi, 1, 0)} color={"primary"} title="Sağa"><ArrowForwardIcon /></Button>}
                                        {seciliSarkiDizilimi !== null && <Button variant="outlined" onClick={() => hareketEttir(seciliSarkiDizilimi, 0, 1)} color={"primary"} title="Aşağı"><ArrowDownwardIcon /></Button>}
                                        {seciliSarkiDizilimi !== null && <Button variant="outlined" onClick={() => trim(seciliSarkiDizilimi)} color={"primary"} title="Trim (sol-üst köşeye yasla)"><VerticalAlignTopIcon /></Button>}
                                        {(seciliSarkiDizilimi !== null && (eksikler.length > 0 || fazlalar.length > 0)) && <Button variant="outlined" onClick={() => eksikFazlaDuzelt(seciliSarkiDizilimi)} color={"primary"}>Eksik Fazla Düzelt</Button>}
                                    </>
                                }
                            </Columns>
                            <Rows flex style={{ visibility: seciliSarkiDizilimi ? undefined : "hidden" }}>
                                <DizilimDisplayer readonly={!yetkiVar} dizilim={seciliSarkiDizilimi} hesaplar={konserinHesapBilgileri} genelDurumHelper={genelDurumHelper} dizilimChanged={e => dizilimChanged(e)} />
                            </Rows>
                        </Rows>
                    }
                </Columns>
            </Rows>
        </SubPageLayout>
    );
}

export default KonserDizilimScreen;

function yeniDizilimOlustur(sarkiId: string | null, konserId: string, konserinHesapBilgileri: { hesap: HesapBilgiVeKullaniciModel, sesGrup: SesGrupModel }[], genelDurumHelper: GenelDurumHelper) {
    const yeniDizilim = new DizilimModel();
    yeniDizilim.aciklama = "";
    yeniDizilim.konserId = konserId!;
    yeniDizilim.sarkiId = sarkiId;
    yeniDizilim.koroModelId = genelDurumHelper.SeciliHesapBilgiModel.koroModelId;
    yeniDizilim.id = GuidHelper.NewGuid();
    yeniDizilim.olusturan = genelDurumHelper.genelDurum.kullanici.kullaniciAdi;

    yeniDizilim.dizilimHesapModelList = [];
    const groups = konserinHesapBilgileri.groupBy(e => e.sesGrup.id);
    const groupKeys = groups.select(e => e.key).distinct();
    const katsayi = Math.ceil(BusinessHelper.KonserDizilim.AvatarWidth / BusinessHelper.KonserDizilim.DizilimMainUnitInPixels);

    for (let siraY = 0; siraY < groupKeys.length; siraY++) {
        const grubunKoristleri = groups.single(e => e.key === groupKeys[siraY]).list;
        const listToAdd = grubunKoristleri.map((e, i) => dizilimHesapOlustur(i * katsayi, siraY * katsayi, yeniDizilim, e.hesap, genelDurumHelper));
        yeniDizilim.dizilimHesapModelList.push(...listToAdd);
    }

    return yeniDizilim;
}

function dizilimHesapOlustur(siraX: number, siraY: number, yeniDizilim: DizilimModel, kullanici: HesapBilgiVeKullaniciModel, genelDurumHelper: GenelDurumHelper): DizilimHesapModel {
    const result = new DizilimHesapModel();

    result.id = GuidHelper.NewGuid();
    result.dizilimId = yeniDizilim.id;
    result.hesapId = kullanici.id;
    result.locationX = siraX;
    result.locationY = siraY;
    result.olusturan = genelDurumHelper.genelDurum.kullanici.kullaniciAdi;
    result.olusturmaTarih = DateTimeHelper.now();

    return result;
}